import { serverTimestamp } from "firebase/database";

import db_lib from 'xAppLib/libs/db_lib';
import { rejectOnResErr, toDictionary } from "../views/icosm/treatment/utils";
import {ensureIsError, rejectWithResErr} from "../xAppLib/helpers/rejectWithResErr";
import obj_diff from "../xAppLib/helpers/obj_diff";
import API_service from 'xAppLib/providers/API_service'
import logger from "../xAppLib/libs/logger";
import med_model from "./med_model";
import user_model from "./user_model";
import { waiting_room_model } from "./waiting_room_model";

const DEBUG = false;


const _FRDB_LOC = 'WR_instcons_cosm';

// const _MODEL_NAME = 'instcons_model_base';

// ---- --------------------------------------------  --------------------------------------------
// ---- --------------------------------------------  --------------------------------------------

export default class instcons_model_base extends db_lib {

	static get FRDB_LOC() { return _FRDB_LOC; }

	// static get NEW_USER_FORM_FIELDS() { return _NEW_USER_FORM_FIELDS; }
	// static get MODEL_NAME() { return _MODEL_NAME; }

// ---- --------------------------------------------  --------------------------------------------
// ---- --------------------------------------------  --------------------------------------------

	static get_pending_calls() {
		return API_service.load_data('instcons/pending_calls');
	}


	static join_handler({token,peer_id,uid,name,stream_id}){
		this.set_record (token+'/peers/'+peer_id, {uid,name,stream_id})
		return ()=>{
			console.log("Leaving",peer_id);
			this.set_record (token+'/peers/'+peer_id, null)
		}

	}

	static create_session() {
		return API_service.load_data('instcons/create_session', {}/*force post*/)
	}

	static get_token(sessionId) {
		return API_service.load_data('instcons/get_token',{sessionId})
	}

	static async requireConsult(ic_wr_key) {
		return this.update_record(
			ic_wr_key,
			{reqcons: true},
			{add_hist: true}
		);
	}

	static async update_status(ic_wr_key, sid, status, msg) {
		const clear_doc = ['instcons_await', 'instcons_cancel'].includes(status);
		const is_approved = status==='instcons_approved';
		const start = Date.now();
		const updateFrdb = async () => {
			await this.update_record(
				ic_wr_key + '/',
				{
					status: status,
					...(clear_doc
							? {reqcons: null, doc: null}
							: {
								doc: {
									doc_id: app.user.claims.doc_id,
									nm: app.user.user_det.displayName,
									em: app.user.user_det.email,
									...(msg && {msg})
								}
							}
					),
				},
				{add_hist: true}
			);
		}

		const callApi = async () => {
			const payload = {
				sid,
				stat: status,
				doc: clear_doc ? null : app.user.claims.doc_id,
				msg,
				is_scr: is_approved
			};

			const res = await API_service.load_data( 'chgStat', payload);
			if (res?.res !== 'ok') {
				logger.report_error('instcons_model_base.update_status(chgStat)', 'error', {status, ic_wr_key, sid, res});
			}
			return res;
		};

		const updatePgdb = async () => {
			console.log('instcons_model_base.update_status', ic_wr_key, status, Date.now()-start);
			try {
				return await callApi();
			} catch (err) {
				logger.report_error('instcons_model_base.update_status(chgStat)', 'error', {status, ic_wr_key, sid, err});
				try {
					return await callApi()
				} catch (retry) {
					logger.report_error('instcons_model_base.update_status(chgStat)', 'error', {status, ic_wr_key, sid, retry});
					return Promise.reject(retry);
				}
			} finally {
				console.log('instcons_model_base.update_status', sid, status, Date.now()-start);
			}
		}

		await updateFrdb();

		return await updatePgdb();
	}

	static watch_incomplete(onData, onError) {
		const subscriptions = {};
		const full_records = {};

		const stop_watching = waiting_room_model.watch_cosm(
			(_records) => {
				const records = _records ?? {};

				// The waiting room is keyed by sid, but cosm uses firebase push IDs, so need to remap.
				const byIcWrKey = toDictionary(
					Object.values(records).filter(x => x.ic_wr_key),
					x => x.ic_wr_key,
				);

				const added = Object.keys(byIcWrKey).filter(key => !(key in subscriptions));
				const removed = Object.keys(subscriptions).filter(key => !(key in byIcWrKey));

				for (const ic_wr_key of added) {
					// New record in the waiting room? Subscribe.
					// Since we're watching a specific record (well, set of records) this shouldn't be putting too
					// much load on the client or rtdb. Besides, we're making a bunch of API calls to BE _as well_

					DEBUG && console.log('waiting_room_model::watch_cosm / adding subscription', ic_wr_key);
					subscriptions[ic_wr_key] = this.watch_record(ic_wr_key, full => {
						full_records[ic_wr_key] = full;
						DEBUG && console.log('waiting_room_model::watch_cosm / full records', full_records);
						onData(full_records);
					});
				}

				for (const ic_wr_key of removed) {
					// Dropped out of the waiting room? Unsubscribe.
					DEBUG && console.log('waiting_room_model::watch_cosm / remove subscription', ic_wr_key);
					subscriptions[ic_wr_key]();

					delete subscriptions[ic_wr_key];
					delete full_records[ic_wr_key];
				}

				onData(full_records);
			},
			onError,
		);

		return () => {
			stop_watching();

			// unsubscribe from everything.
			for (const ic_wr_key in subscriptions) {
				subscriptions[ic_wr_key]();
			}
		};
	}

	static async recreate_session(ic_wr_key) {
		if (!ic_wr_key) {
			return Promise.reject('Missing ic_wr_key');
		}
		return this.create_session().then(rsp => (rsp.sessionId
			? this.update_record(ic_wr_key, {sessionId: rsp.sessionId})
			: Promise.reject('No session created')
		));
	}

	// Returns others that appear to be part of a multi-treatment requests given some "main" record
	// e.g. if records a and b are similar but not c, then:
	// 	- get_similar_records(a, [b,c]) => [b]
	// 	- get_similar_records(b, [a,c]) => [a]
	// 	- get_similar_records(c, [a,b]) => []
	// also, while a is "similar" (identical!) to itself, that's not helpful, so will be excluded from the list:
	// 	- get_similar_records(a, [a]) => []
	static get_similar_records(main, others = []) {
		if (!main) return [];
		if (this.is_treatment_plan(main)) return [];

		const same_or_nullish = (x = null, y = null) => x === y || x === null || y === null;

		return others.filter(other => (
			!this.is_treatment_plan(other) &&
			other !== main &&
			other.sid !== main.sid &&
			other.user_iding === main.user_iding &&
			// must have an auth.uid, if it's nullish this record is invalid
			(other.auth?.uid && other.auth.uid === main.auth?.uid) &&
			// must both be assigned to the _same_ doc, or one/both can be unassigned.
			// if both are assigned to different docs, that's weird, but don't show them as similar records
			same_or_nullish(other.doc?.doc_id, main.doc?.doc_id)
		))
	}

	static needs_consult(record) {
		const script_type = record.script_type || 'instcons';
		const others = record.others || [];

		const needs_consult = (
			script_type === 'instcons'
			|| script_type === 'doccons'
			|| record.reqcons
			|| record.is_review === false
		);

		return needs_consult || others.some(other => this.needs_consult(other));
	}

	static is_pending(record) {
		return ['instcons_await', 'instcons_taken'].includes(record.status);
	}

	static is_final(record) {
		return ['instcons_approved', 'instcons_rejected', 'instcons_cancel', 'done_doccall', 'auto_approved', 'done_auto'].includes(record.status);
	}

	static consult_state(row) {
		const {
			status,
			sessionId,
			doc,
		} = row;

		const inWaitingRoom = status === 'instcons_await';
		const isTaken = status === 'instcons_taken';
		const invalidSession = String(sessionId).indexOf('error') === 0;
		const canOfferDrSkypeFallback = Boolean(isTaken && doc) && app.site_status.instcons_dr_skype_fallback_delay > 0;
		// if we're offering the consulting dr skype, don't show other doctors' skype
		const canOfferSkype = !canOfferDrSkypeFallback && inWaitingRoom && app.site_status.instcons_skype_fallback_delay > 0;
		const canCancel = inWaitingRoom;
		const consult_heading = doc
			? `Consultation with ${doc.nm}`
			: 'Awaiting consultation';
		const review_heading = doc
			? `Being reviewed by ${doc.nm}`
			: 'Awaiting review';

		const heading = this.needs_consult(row)
			? consult_heading
			: review_heading;

		return {
			inWaitingRoom,
			isTaken,
			invalidSession,
			canOfferDrSkypeFallback,
			canOfferSkype,
			canCancel,
			heading,
		};
	}

	static async set_multirequest(wr_key, multi = true) {
		await this.update_record(`${wr_key}/req`, {multi}, {add_hist: true});
	}

	static last_multirequest(record) {
		// Check the most recent script for this patient and see if it has been flagged
		// as a multi-request. Indicates the nurse is filling out another script for the
		// same patient and the doctor should wait before taking the consult

		const last = [record, ...record?.others || []].slice(-1)[0];

		return last?.req?.multi ? last : null;
	}

	static consult_route(record) {
		if (this.is_treatment_plan(record)) {
			return `/cosmetics/consult/${record.sid}`;
		}

		return `/instcons_doc/${record.sid}/${record.key ?? record.cosm_det?.ic_wr_key}`;
	}

	static is_legacy_script(record) {
		return !this.is_treatment_plan(record);
	}

	static is_treatment_plan(record) {
		return ['tpcons', 'tprev', 'tprpt', 'tptopup'].includes(record.script_type);
	}

	static last_skype_call(record) {
		return Object.values(record.logs || {}).filter(x => x.label==='skype_call').slice(-1)[0]
	}

	static async take_treatment_plan(record) {
		await this.update_record(
			`${record.key}/`,
			{
				status: 'instcons_taken',
				doc: {
					doc_id: app.user.claims.doc_id ?? 'nodoc',
					nm: app.user.user_det.displayName,
					em: app.user.user_det.email,
				}
			},
			{add_hist: true}
		);

		const result = await API_service.api_promise(`cosmetics/treatment/${record.sid}/take`, {}).then(rejectOnResErr);
		return result;
	}

	static seen = {};

	static async set_seen(record, wr_load_time) {
		const key = record.key;

		if (!['instcons_await', 'instcons_taken'].includes(record.status)) return;

		// We're updating a record in response to loading the record, which will cause an update to the record
		// which... if something goes wrong will cause bad times. So at least for this browser session, use a
		// dirty cache to only ever attempt this once.
		if (this.seen[key]) return;
		this.seen[key] = true;

		// I'm just tracking to the device level in rtdb, but if they have multiple tabs open will only get the first
		// to render the consult. I could track to the session level, but that means we'll get a new record if they
		// reload the page, which is probably over kill. Still, log it in case we need to debug.
		logger.usg_log('instcons_model_base.set_seen', location.pathname, null, {key})

		const uid = app.user.uid;
		const dvcid = app.dvc?.dvcid ?? 'nodvc';

		if (record?.seen?.[uid]?.[dvcid]) return;

		const path = `${key}/seen/${uid}/${dvcid}`;
		const times = {
			l: wr_load_time,
			// server vs client time. Will help to determine network latency or client clock drift... maybe?
			s: serverTimestamp(),
			c: Date.now(),
		};

		return await this.update_record(path, times).catch(err => {
			logger.report_error('instcons_model_base.set_seen', 'error', {err, path, uid, times});
		});
	}

	static async list_treatment_plans({page, pageSize, ...filters}) {
		const q = new URLSearchParams();

		q.set('offset', String((page - 1) * pageSize));
		q.set('limit', String(pageSize));

		return await API_service.api_promise(`cosmetics/treatments/list?${q}`, filters).then(rejectOnResErr);
	}

	static treatment_area_name(areaId) {
		return med_model.COSM_TREATMENT_AREA.find(x => x.key === areaId)?.text ?? areaId;
	}

	static formatProducts(products){
		return products.map((product) => ({
			...product,
			...('areas' in product && {
				areas: product.areas
					.map(areaId => ({
						areaId,
						name:  this.treatment_area_name(areaId) ?? areaId,
					}))
					.sort((x, y) => {
						return x.name.localeCompare(y.name)

					}),
			}),
		}))
	}

	static async load_revisit_treatment_form_config(sid, type) {
		return await API_service
			.api_promise(`cosmetics/treatment/${sid}/${type}`)
			.then(rejectWithResErr,  ensureIsError)
			.then(r => r.data)
			.then(data => ({
				...data,
				products: data.products.map(product => ({
					...product,
					...('areas' in product && {
						areas: product.areas
							.map(area => ({
								...area,
								name: area.name ?? this.treatment_area_name(area.areaId),
							}))
							.sort((x, y) => x.name.localeCompare(y.name)),
					}),
				})),
			}));
	}

	static async load_initial_consult_form_config() {
		const cat_nm = 'cosm';

		return await API_service
			.api_promise('cosmetics/treatments/initial-consult-config')
			.then(rejectWithResErr, ensureIsError)
			.then(r => r.data)
			// .then(data => /* FIXME */ data.dispenseMode === 'pharmacy' ? Promise.reject(new Error('Pharmacy dispense mode not implemented yet')) : data)
			.then(data => ({
				...data,
				products: this.formatProducts(data.products || []).filter(({cid: k}) => user_model.check_access('show_medcat', {k, cat_nm, ignore_cache: true})),
			}));
	}

	static async load_order_product_config(sid, tentative_treatment_date) {
		const url = new URL(`cosmetics/treatment/${sid}/product-order`, location.origin);
		const query = tentative_treatment_date ? `?${new URLSearchParams({ tentative_treatment_date })}` : "";
		return await API_service.api_promise(`cosmetics/treatment/${sid}/product-order${query}`).then(rejectOnResErr).then(r => r.data);
	}

	static async load_revisit_product_order_config(sid, type, tentative_treatment_date) {
		const query = tentative_treatment_date ? `?${new URLSearchParams({ tentative_treatment_date })}` : "";
		return await API_service.api_promise(`cosmetics/treatment/${sid}/${type}/product-order${query}`)
			.then(rejectOnResErr)
			.then(r => r.data)
			.then(data => ({
				...data,
				...(data.products && {
					products: data.products.map(product => ({
						...product,
						...('areas' in product && {
							areas: product.areas
								.map(area => ({
									...area,
									name: area.name ?? this.treatment_area_name(area.areaId),
								}))
								.sort((x, y) => x.name.localeCompare(y.name)),
						}),
					})),
				}),
			}));
	}

	static async cancel_treatment_plan(sid) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/cancel`, {}).then(rejectOnResErr);
	}

	static async release_treatment_plan(sid) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/release`, {}).then(rejectOnResErr);
	}

	static async add_treatment_plan_notes(sid, note) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/post-consult`, {note}).then(rejectOnResErr);
	}

	static async record_administered_product(sid, administered) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/inventory`, {administered}).then(rejectOnResErr);
	}

	static async amend_treatment_plan(sid, form) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/amend`, form).then(rejectOnResErr);
	}

	static async annul_direction_to_administer(sid, form) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/annul`, form).then(rejectOnResErr);
	}

	static async load_insights(sid) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/insights`).then(rejectOnResErr);
	}

	static async update_treatment_plan_photos(sid, before, after) {
		const data_diff = obj_diff(before, after);

		if (!data_diff.to || !Object.keys(data_diff).length) {
			return
		}

		const params = {
			sid,
			phts: after,
			diff: {
				...data_diff,
				part: 'phts'
			}
		}

		return await API_service.api_promise('scripts/upd_script', params).then(rejectOnResErr);
	}

	static async complete_treatment_plan(sid, form = {}) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/complete`, form).then(rejectOnResErr);
	}

	static async change_decisions(sid, form = {}) {
		return await API_service.api_promise(`cosmetics/treatment/${sid}/change-decisions`, form).then(rejectOnResErr);
	}

	static async set_vid_provider(record, vid_provider) {
		return await this.update_record(record.key, {vid_provider});
	}

	static async set_log_level(record, log_level) {
		return await this.update_record(record.key, {log_level: log_level || null});
	}

	static async add_log(ic_wr_key, label, args = {}) {
		const record = {
			...JSON.parse(JSON.stringify(args)), label, tm: Date.now(),
		};
		return await this.add_record(`${ic_wr_key}/logs`, record);
	}

	static async notify_c2u(planId, treatmentId) {
		return await API_service.api_promise(`cosmetics/treatment/${planId}/notify-c2u`, { treatmentId }).then(rejectOnResErr);
	}

	static async notify_pharmacy(planId, treatmentId) {
		return await API_service.api_promise(`cosmetics/treatment/${planId}/notify-pharmacy`, { treatmentId }).then(rejectOnResErr);
	}

	static async create_escripts(planId) {
		return await API_service.api_promise(`cosmetics/treatment/${planId}/create-escripts`, {}).then(rejectOnResErr);
	}

	static async sync_org_data(planId) {
		return await API_service.api_promise(`cosmetics/treatment/${planId}/sync-org-data`, {}).then(rejectOnResErr);
	}
}
